import React, { Component, useContext } from 'react';
import PropTypes from 'prop-types';
import { makeContextCacher } from 'common/utils/context';
import * as tours from './actions/tours';
import * as fetchActions from './actions/fetch';
import * as prepAndSend from './actions/prep-and-send';
import * as templates from './actions/templates';
import * as deepIntegration from './actions/deep-integration';
import * as bulkSend from './actions/bulk-send';
import signatureActions from './actions/signature';
import * as admin from './actions/admin';
import * as editor from './actions/editor';
import * as moar from './actions/moar';
import * as adminServices from 'admin/services';
import * as sharepoint from './actions/sharepoint';
import * as docToTemplate from './actions/doc-to-template';
import * as postActions from './actions/post';
import * as docSummary from './actions/document-summary';
import * as dropboxProPro from './actions/dropbox-pro-pro';
import * as accountSettings from './actions/account-settings';
import * as help from './actions/help';
import * as signer from './actions/signer';
import uuid from 'uuid';
import { appActionsPanel } from 'hello-react/components/app-actions-panel';
import { isProduction } from 'hellospa/ts-utils';
import * as integration from './actions/integration';

const log: AppActionLogEntry[] = [];

/*
  The goal of AppActions is to abstract the way we talk to the server.
  Redux Action Creators, Components, and tests never have to know or care about
  implementation details:
  * URL
  * GET or POST
  * Do parameters go in the query or body?
  * If body, is it JSON encoded or Form parameters?

  Actions are just ordinary functions that return a promise. This also makes it
  easy to write mock actions and start writing UI code while the backend is
  still being built. That also makes them easy to mock in tests, becuase you
  only really care about the input and output. Tests shouldn't really care about
  implementation details.

*/
export type NamespacedAppActions = {
  // Please use tours as an example for new namespaces.
  adminServices: typeof adminServices,
  moar: typeof moar,
  prepAndSend: typeof prepAndSend,
  tours: typeof tours,
  editor: typeof editor,
  bulkSend: typeof bulkSend
  postActions: typeof postActions
  deepIntegration: typeof deepIntegration
  admin: typeof admin
  docSummary: typeof docSummary
  docToTemplate: typeof docToTemplate
  fetchActions: typeof fetchActions
  templates: typeof templates,
  sharepoint: typeof sharepoint,
  dropboxProPro: typeof dropboxProPro,
  help: typeof help,
  signer: typeof signer,
  accountSettings: typeof accountSettings,
  integration: typeof integration,
}

export type LegacyAppActions = (
  /*
   * All these get merged together into a single object. appActions.loadData
   * comes from editorActions and appActions.getPrepAndSendData comes from
   * prepAndSendActions. Follow the tours example to get
   * appActions.{yourNamespaceHere}.*
   */
  & typeof fetchActions
  & typeof prepAndSend
  & typeof deepIntegration
  & typeof sharepoint
  & typeof docToTemplate
  & typeof postActions
  & typeof dropboxProPro
  & typeof integration
  & ReturnType<typeof signatureActions>
)


export type AppActions = NamespacedAppActions & LegacyAppActions

export const namespaceWarning = <T extends object>(namespace: string, t: T): T => {
  if (
    process.env.NODE_ENV === 'test'
    || process.env.IS_STORYBOOK
    || (
      process.env.NODE_ENV === 'development'
      && window.location.hostname.includes('dev-hellosign')
    )
  ) {

    // Grab the keys from a regular objecct
    let keys: Array<keyof T> = Object.keys(t) as Array<keyof T>;

    // But if the namespace has a prototype, assume it's a class and read its
    // methods. In an earlier version I only read the prototype if the keys were
    // empty, but that means if the class assigned any properties to the
    // instance, then there would be keys and it didn't pick up the methods.
    if (Object.getPrototypeOf(t) !== Object.getPrototypeOf({})) {
      keys = keys.concat(
        Object.getOwnPropertyNames(Object.getPrototypeOf(t)) as Array<keyof T>,
      );
    }

    keys = keys.filter(
      (key) => !String(key).includes('___'),
    );

    const entries = keys.map((key) => {
      const value = t[key];
      if (typeof value === 'function') {
        return [
          key,
          function deprecated(...args: any) {
            // eslint-disable-next-line no-console
            console.warn(`Please use appContext.${namespace}.${key} instead of appContext.${key}`);
            // I'm calling this with it's original context so that if a
            // namespace is storing any information internally, we don't end up
            // with 2 different instances.
            return value.apply(t, args);
          },
        ];
      }
      return [key, value];
    });

    return Object.fromEntries(entries) as T;
  }
  return t;
};

// @ts-ignore I can't really create a default context here
export const appContext: React.Context<AppActions> = React.createContext({});

interface AppContextProp {
  appContext: AppActions
}

export function withAppContext<P>(
  Component: React.ComponentType<P & AppContextProp & React.RefAttributes<{}>>,
) {
  // Using a named function will name the forwarded ref.
  // https://reactjs.org/docs/forwarding-refs.html#displaying-a-custom-name-in-devtools
  // eslint-disable-next-line react/display-name
  return React.forwardRef((props: P, ref: React.Ref<any>) => {
    return (
      <appContext.Consumer>
        {(appContext: AppActions) => (
          <Component {...props} appContext={appContext} ref={ref} />
        )}
      </appContext.Consumer>
    );
  });
}

interface User {

}

interface Props {
  csrfToken: string,
  csrfTokenAttachmentDelete: string,
  preloadedTsmGroupKey: string,
  initialUser?: User
  createMock?: <T>(key: keyof AppActions, orig: T) => T
}

interface State {
  // TODO: Remove this. It's leftover from before we started using Redux.
  user?: User
}

export default class AppContext extends Component<Props, State> {
  static propTypes = {
    csrfToken: PropTypes.string.isRequired,
    csrfTokenAttachmentDelete: PropTypes.string,
    preloadedTsmGroupKey: PropTypes.string,
    initialUser: PropTypes.shape({
      primarySignatureGuid: PropTypes.string,
      settings: PropTypes.shape({
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
      }),
    }),
    children: PropTypes.node,
    createMock: PropTypes.func,

    termsURL: PropTypes.string,
    privacyURL: PropTypes.string,
  };

  state = {
    user: this.props.initialUser,
  };

  contextCache = makeContextCacher<AppActions & { data: State }>();

  actions: AppActions;

  constructor(props: Props) {
    super(props);
    this.actions = this.buildActions();
  }

  buildActions = () => {
    editor.__appContextInternal.setPreLoadedTsmGroupKey(
      this.props.preloadedTsmGroupKey,
    );

    const actions: AppActions = {
      ...namespaceWarning('fetchActions', fetchActions),
      ...namespaceWarning('prepAndSend', prepAndSend),
      ...namespaceWarning('deepIntegration', deepIntegration),
      ...namespaceWarning('bulkSend', bulkSend),
      ...signatureActions(this.props.preloadedTsmGroupKey),
      ...namespaceWarning('admin', admin),
      ...namespaceWarning('docToTemplate', docToTemplate),
      ...namespaceWarning('sharepoint', sharepoint),
      ...namespaceWarning('postActions', postActions),
      ...namespaceWarning('dropboxProProActions', dropboxProPro),
      ...namespaceWarning('integration', integration),
      adminServices,
      bulkSend,
      editor,
      fetchActions,
      moar,
      accountSettings,
      deepIntegration,
      docToTemplate,
      postActions,
      prepAndSend,
      sharepoint,
      integration,
      tours,
      docSummary,
      admin,
      templates,
      dropboxProPro,
      help,
      signer,
    };

    const createMock = this.props.createMock;
    if (createMock != null) {
      return Object.keys(actions).reduce((actions, key) => {
        // @ts-ignore
        actions[key] = createMock(key, actions[key]);
        return actions;
      }, actions);
    }
    return this.logAppActionsInDevelopment(actions);
  };

  /**
   * This function walks the top 2 layers of appActions and wraps any functions
   * so that they can be logged to window.appActionLog. It was helpful in
   * converting Editor's namespace so I could see what actually is being called
   * and its responses.
   */
  logAppActionsInDevelopment = (original: any, path: string[] = []) => {
    if (isProduction()) {
      return original;
    }

    const unwrapResult = async (result: any) => {
      try {
        return await result;
      } catch (e) {
        if (e instanceof Error) {
          return `Error: ${e.message}`;
        }
      }
    };

    const logResult = async (
      { name, args, result }: Pick<AppActionLogEntry, 'name' | 'args' | 'result'>,
    ) => {
      const detail: AppActionLogEntry = {
        id: uuid(),
        timestamp: (new Date()).toISOString(),
        name,
        args,
        result: await unwrapResult(result),
      };


      log.push(detail);
      while (log.length > 20) {
        log.shift();
      }

      document.body.dispatchEvent(
        new CustomEvent('app-action-log', { detail: [...log] }),
      );
    };

    // Ideally we'd move these out of app actions, but for now I'm just going to
    // black list them so we don't log calls to local validation functions.
    const schemaObjects = [
      'dataSchema',
      'templateResponse',
      'templateErrorResponse',
    ];

    return Object.keys(original).reduce((actions, key) => {
      const value = actions[key];
      if (
        value == null
        || schemaObjects.includes(key)
      ) {
        // skip it
      } else if (typeof value === 'function') {
        actions[key] = (...args: any) => {
          const result = value(...args);


          const name = path.concat([key]).join('.');
          logResult({
            name,
            args,
            result,
          });

          return result;
        };
      } else if (typeof value === 'object' && path.length < 1) {
        actions[key] = this.logAppActionsInDevelopment(
          actions[key],
          path.concat([key]),
        );
      }

      return actions;
    }, original);
  };

  render() {
    const context = this.contextCache(Object.assign({
      data: this.state,
    }, this.actions));

    return (
      <appContext.Provider value={context}>
        {appActionsPanel}
        {this.props.children}
      </appContext.Provider>
    );
  }
}

export type AppActionLogEntry = {
  id: string
  timestamp: string
  name: string,
  args: unknown[],
  result: unknown | Promise<unknown>,
}

export function useAppContext() {
  const context = useContext(appContext);
  return context;
}
